import { SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import { Card, CardContent } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router'
import AdminAccounts from '@web/src/features/admin/admin-accounts/admin-accounts'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useCallback } from 'react'
import type { UsersListQueryVariables } from 'types/graphql'
import { z } from 'zod'

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/accounts/')({
  component: AccountsPage,
  validateSearch: z.object({
    page: z.number().optional(),
    sortBy: z.string().optional(),
    search: z.string().optional(),
    roles: z.string().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
        sortBy: 'LATEST',
        search: '',
        roles: 'all',
      }),
    ],
  },
  head: () =>
    seo({
      title: `Admin - Users | ${env.VITE_APP_NAME}`,
      description: 'Admin Users Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
})

function AccountsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = useSearch({ strict: false })

  const params = {
    page: search.page || 1,
    sortBy: (search.sortBy as string) || 'LATEST',
    search: search.search || '',
    roles: search.roles || 'all',
  }

  const pageInt = search.page ? Number.parseInt(String(search.page), 10) : 1

  const formMethods = useForm({
    defaultValues: {
      sortBy: params.sortBy,
      search: params.search,
      roles: params.roles,
    },
  })

  const filterResults = useCallback(() => {
    const sortBy = formMethods.getValues('sortBy')
    const searchVal = formMethods.getValues('search')
    const roles = formMethods.getValues('roles')
    navigate({
      search: {
        ...search,
        sortBy,
        search: searchVal,
        roles,
      },
    } as never)
  }, [formMethods, navigate, search])

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-2 py-4 md:px-4">
      <div className="flex w-full flex-col gap-4">
        <Form className="w-full" formMethods={formMethods} onSubmit={() => filterResults()}>
          <SearchExpand
            className="w-full"
            clearSearchLabel={t('Clear search')}
            fieldProps={{ className: 'min-w-0 flex-1' }}
            inputProps={{ 'aria-label': t('Search accounts') }}
            minChars={0}
            name="search"
            onSearchChange={filterResults}
            placeholder={t('Search accounts...')}
            toggleFiltersLabel={t('Toggle filters')}
          >
            <Card className="gap-0 py-0">
              <CardContent className="px-2 py-3">
                <div className="flex w-full flex-col gap-4">
                  <div className="flex w-full flex-wrap items-start gap-2">
                    <NativeSelectFormField
                      fieldProps={{ className: 'min-w-0 flex-1 w-auto sm:min-w-[10rem]' }}
                      label={t('Role')}
                      name="roles"
                      onValueChange={() => {
                        filterResults()
                      }}
                    >
                      <option value="all">{t('All')}</option>
                      <option value="admin">{t('Admin')}</option>
                      <option value="member">{t('Member')}</option>
                      <option value="moderator">{t('Moderator')}</option>
                      <option value="preview">{t('Preview')}</option>
                      <option value="supporter">{t('Supporter')}</option>
                      <option value="tradeshow">{t('Tradeshow')}</option>
                    </NativeSelectFormField>

                    <NativeSelectFormField
                      fieldProps={{ className: 'min-w-0 flex-1 w-auto sm:min-w-[10rem]' }}
                      label={t('Sort By')}
                      name="sortBy"
                      onValueChange={() => {
                        filterResults()
                      }}
                    >
                      <option value="BANNED">{t('Banned')}</option>
                      <option value="DELETED">{t('Deleted profiles')}</option>
                      <option value="LATEST">{t('Latest')}</option>
                      <option value="MEMBERSHIP_ASC">{t('Role ascending')}</option>
                      <option value="MEMBERSHIP_DESC">{t('Role descending')}</option>
                      <option value="NAME_ASC">{t('Name ascending')}</option>
                      <option value="NAME_DESC">{t('Name descending')}</option>
                      <option value="POINTS_ASC">{t('Points ascending')}</option>
                      <option value="POINTS_DESC">{t('Points descending')}</option>
                      <option value="OLDEST">{t('Oldest')}</option>
                    </NativeSelectFormField>
                  </div>
                </div>
              </CardContent>
            </Card>
          </SearchExpand>
        </Form>

        <AdminAccounts
          page={pageInt}
          roles={params.roles}
          search={params.search}
          sortBy={params.sortBy as UsersListQueryVariables['sortBy']}
        />
      </div>
    </div>
  )
}
